import {
  createRegistration,
  linkCredential,
  linkRegistrationDocument,
  unlinkRegistrationDocument,
  updateRegistration,
  useCredentials,
  useRegistration,
  useRegistrations,
  useSupportedRegistrations,
  useJurisdictions,
} from '@commenda/api';
import {
  Button,
  ComboboxWithSearch,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Icons,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleFileUploader,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  CreateRegistrationProps,
  RegistrationApplicability,
  RegistrationDocumentRole,
} from '@commenda/types/src/registration/types';
import { RegistrationHelper } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Country,
  LegacyDocumentType,
  RegistrationValidationStatus,
} from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useJurisdictionLookup } from '../../../../../hooks/useJurisdictionLookup';
import { useNotification } from '../../../../../hooks/useNotification';
import {
  addFQRegistrationFormSchema,
  AddFQRegistrationFormSchemaFields,
} from './addRegistrationSchema';

export const AddFQRegistrationModal = ({
  isOpen,
  onClose,
  onSuccess,
  businessEntity,
  mode = 'create',
  registrationId,
  companyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessEntity: {
    id: number;
    country: Country;
  };
  /** Which form this is. Explicit so a stale id cannot turn a create into an edit. */
  mode?: 'create' | 'edit';
  registrationId?: string;
  companyId: number;
}) => {
  const isEditMode = mode === 'edit';
  const { data: registrationResponse, error: registrationError } =
    useRegistration(businessEntity.id, registrationId);

  const {
    data: supportedRegistrationsResponse,
    error: supportedRegistrationsError,
  } = useSupportedRegistrations({
    businessEntityId: businessEntity.id,
  });
  const { optionsFor } = useJurisdictions();
  const { isLoaded: isJurisdictionContentLoaded, isCountryLevel } =
    useJurisdictionLookup();

  const { data: credentialsResponse, error: credentialsError } = useCredentials(
    {
      businessEntityId: businessEntity.id,
    }
  );

  const supportedRegistrations =
    supportedRegistrationsResponse?.supportedRegistrations || [];

  const { data: registrations, error: registrationsError } = useRegistrations(
    businessEntity.id
  );

  const { setNotification } = useNotification();
  const isSubmitting = useToggle();
  const [isReplacingLegacyDocument, setIsReplacingLegacyDocument] =
    useState(false);

  const methods = useForm<AddFQRegistrationFormSchemaFields>({
    resolver: zodResolver(addFQRegistrationFormSchema),
    defaultValues: {
      registrationName: '',
      jurisdiction: '',
      effectiveDate: '',
      status: null,
      linkedCredential: '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    clearErrors,
  } = methods;

  const [selectedRegistrationIdentifier, setSelectedRegistrationIdentifier] =
    useState<string | null>(null);

  const isCredentialDisabled = !!(
    isEditMode &&
    registrationResponse?.registration?.credentials?.length &&
    registrationResponse.registration.credentials.length
  );
  const registrationDocuments = isEditMode
    ? registrationResponse?.registration.documents ?? []
    : [];
  const existingDocument = registrationDocuments.find(
    (document) => document.role === RegistrationDocumentRole.PRODUCED
  );
  const legacyDocument = !isEditMode || registrationDocuments.length
    ? undefined
    : registrationResponse?.registration.governmentIdentifiers?.[0]?.supportingDocuments?.[0];
  const initialDocument = existingDocument ?? legacyDocument;
  const registrationDocument = watch('registrationDocument');
  const isLegacyDocument =
    isEditMode &&
    !!legacyDocument &&
    registrationDocument?.fileUrl === legacyDocument.fileUrl;

  const handleClose = () => {
    setSelectedRegistrationIdentifier(null);
    setIsReplacingLegacyDocument(false);
    reset();
    onClose();
  };

  const onSubmit = async (data: AddFQRegistrationFormSchemaFields) => {
    isSubmitting.on();
    let registrationSaved = false;

    try {
      const body: CreateRegistrationProps = {
        status: data.status as RegistrationValidationStatus,
        country: businessEntity.country,
        // Names no type: the backend takes the one this registration produces.
        governmentIdentifiers: [{ value: data.registrationNumber }],
      };

      if (data.effectiveDate) body.effectiveStartDate = data.effectiveDate;
      if (data.jurisdiction)
        body.jurisdiction = data.jurisdiction;
      body.registrationIdentifier = selectedRegistrationIdentifier;

      let response: Response;
      let createdRegistrationId: string;

      if (isEditMode) {
        const governmentIdentifier =
          registrationResponse?.registration.governmentIdentifiers?.[0];

        response = await updateRegistration(businessEntity.id, registrationId, {
          status: data.status as RegistrationValidationStatus,
          effectiveStartDate: data.effectiveDate || undefined,
          governmentIdentifiers: [
            {
              id: governmentIdentifier.id,
              value: data.registrationNumber,
            },
          ],
        });
      } else {
        response = await createRegistration(businessEntity.id, body);

        if (response.ok) {
          registrationSaved = true;
          createdRegistrationId = (await response.json()).registration.id;
        }
      }

      if (!response.ok) {
        if (response.status === 409) {
          setNotification({
            show: true,
            valid: false,
            title: 'Registration already exists',
            message:
              'This registration already exists for this business. Please edit the existing registration.',
          });
        } else {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message:
              'Something went wrong. Please try again or contact support.',
          });
        }

        isSubmitting.off();
        return;
      }
      registrationSaved = true;

      const hasNewDocument =
        !!data.registrationDocument.fileUrl &&
        data.registrationDocument.fileUrl !== initialDocument?.fileUrl;
      let linkedDocumentId: number | undefined;
      if (hasNewDocument) {
        const linkedDocument = await linkRegistrationDocument(
          businessEntity.id,
          createdRegistrationId || registrationId,
          {
            role: RegistrationDocumentRole.PRODUCED,
            fileUrl: data.registrationDocument.fileUrl,
            fileName: data.registrationDocument.fileName,
          }
        );
        if (!linkedDocument.success) {
          handleClose();
          onSuccess();
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message:
              'Registration saved, but the document could not be attached. Edit the registration to retry.',
          });
          isSubmitting.off();
          return;
        }
        linkedDocumentId = linkedDocument.data.fileId;
      }

      if (
        existingDocument &&
        hasNewDocument &&
        existingDocument.fileId !== linkedDocumentId
      ) {
        const unlinkResponse = await unlinkRegistrationDocument(
          businessEntity.id,
          registrationId,
          existingDocument.fileId,
          RegistrationDocumentRole.PRODUCED
        );
        if (!unlinkResponse.success) {
          handleClose();
          onSuccess();
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message:
              'Registration saved, but the previous document could not be unlinked. Edit the registration to retry.',
          });
          isSubmitting.off();
          return;
        }
      }

      if (data.linkedCredential && !isCredentialDisabled) {
        const credentialRegistrationLink = await linkCredential(
          businessEntity.id,
          createdRegistrationId || registrationId,
          data.linkedCredential
        );

        if (!credentialRegistrationLink.ok) {
          handleClose();
          onSuccess();
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message:
              'Registration saved, but the credential could not be linked. Please link it manually.',
          });
          isSubmitting.off();
          return;
        }
      }

      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: isEditMode
          ? 'Registration updated successfully.'
          : 'Registration added successfully.',
      });
      setSelectedRegistrationIdentifier(null);
      reset();
      isSubmitting.off();
      onSuccess();
    } catch (error) {
      console.error('Registration submission error:', error);
      if (registrationSaved) {
        handleClose();
        onSuccess();
      }
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: registrationSaved
          ? 'Registration saved, but a follow-up request failed. Edit the registration to retry.'
          : 'An unexpected error occurred. Please try again.',
      });
      isSubmitting.off();
    }
  };

  useEffect(() => {
    // Waits for the content, which is what says whether a registration is country-wide.
    if (!selectedRegistrationIdentifier || !isJurisdictionContentLoaded) return;

    const selectedRegistration = supportedRegistrations.find(
      (reg) => reg.registrationIdentifier === selectedRegistrationIdentifier
    );

    // A country-wide registration applies wherever the entity sits, so the one asked for above
    // stands. Only a registration that narrows to a subdivision replaces it.
    if (
      !isEditMode &&
      selectedRegistration?.jurisdictionId &&
      !isCountryLevel(selectedRegistration.jurisdictionId)
    )
      setValue('jurisdiction', selectedRegistration.jurisdictionId);
  }, [selectedRegistrationIdentifier, isJurisdictionContentLoaded]);

  useEffect(() => {
    if (!registrationId || !selectedRegistrationIdentifier) {
      setSelectedRegistrationIdentifier(null);
      reset();
    }
  }, [registrationId]);

  const jurisdictionOptions = useMemo(
    () =>
      optionsFor(businessEntity.country),
    [optionsFor, businessEntity.country]
  );

  useEffect(() => {
    if (
      !isEditMode ||
      !registrationId ||
      !registrationResponse ||
      !jurisdictionOptions
    )
      return;

    const { registration } = registrationResponse;

    if (registration.registrationIdentifier)
      setSelectedRegistrationIdentifier(registration.registrationIdentifier);

    // The options are keyed by content id, so the display name never matched one and the control
    // fell back to its placeholder. `displayValueIfDisabled` is what puts the name on screen.
    setValue(
      'registrationName',
      registration.registrationIdentifier || registration.displayName
    );
    setValue('jurisdiction', registration.jurisdictionId);
    setValue('effectiveDate', registration.effectiveStartDate);
    setValue('status', registration.status);
    setValue('linkedCredential', registration?.credentials?.[0]?.id || '');

    const governmentIdentifier = registration.governmentIdentifiers?.[0];

    if (governmentIdentifier) {
      setValue('registrationNumber', governmentIdentifier.value || '');
    }
    const prefillDocuments = registration.documents ?? [];
    const evidence = prefillDocuments.length
      ? prefillDocuments.find(
          (document) => document.role === RegistrationDocumentRole.PRODUCED
        )
      : governmentIdentifier?.supportingDocuments?.[0];
    setIsReplacingLegacyDocument(false);
    setValue('registrationDocument', {
      fileName: evidence?.fileName ?? '',
      fileUrl: evidence?.fileUrl ?? '',
    });
  }, [
    isEditMode,
    registrationId,
    registrationResponse,
    watch,
    setValue,
    isOpen,
  ]);

  if (
    !credentialsResponse ||
    !supportedRegistrationsResponse ||
    !registrations ||
    (registrationId && !registrationResponse)
  ) {
    if (credentialsError) console.error(credentialsError);
    if (supportedRegistrationsError) console.error(supportedRegistrationsError);
    if (registrationsError) console.error(registrationsError);
    if (registrationError) console.error(registrationError);

    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          className="overflow-y-hidden min-w-[676px]"
          preventCloseOnOutsideClick
        >
          <DialogHeader>
            <DialogTitle>
              {isEditMode
                ? 'Edit foreign qualification registration'
                : 'Add foreign qualification registration'}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-[280px] flex items-center justify-center">
            <LoadingIndicator size={48} />
          </div>
          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const credentials = credentialsResponse.credentials.map((credential) => ({
    value: credential.id,
    label: credential.name,
  }));

  const selectedJurisdiction = watch('jurisdiction');

  const registrationNameOptions = supportedRegistrations
    .filter((reg) => reg.applicability === RegistrationApplicability.FOREIGN)
    // The picker's values are content jurisdiction ids, so the registration is matched on its own id.
    .filter(
      (reg) =>
        !selectedJurisdiction ||
        reg.jurisdictionId === selectedJurisdiction ||
        isCountryLevel(reg.jurisdictionId)
    )
    .map((reg) => ({
      displayedValue: reg.displayName,
      value: reg.registrationIdentifier,
    }));

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="overflow-y-hidden min-w-[676px]"
        preventCloseOnOutsideClick
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit state registration' : 'Add state registration'}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit, (formError) => {
            console.clear();
            console.log(formError);
          })}
        >
          <div className="flex flex-col gap-2xl p-2xl overflow-y-auto max-h-[60vh]">
            {/* Jurisdiction, asked before the registration because it narrows the list */}
            <FieldWrapper>
              <Label>
                Jurisdiction
                <Tooltip
                  content="The state the entity is registering to do business in"
                  side="right"
                >
                  <Icons.InfoCircle className="ml-1 h-4 w-4 inline text-fg-tertiary" />
                </Tooltip>
              </Label>
              <Select
                disabled={isEditMode}
                onValueChange={(value) => {
                  // An edit cannot move the jurisdiction — the control is disabled — so a change
                  // arriving here is the control reconciling a value it has no option for, and
                  // clearing the name on it wipes the registration being edited.
                  if (isEditMode) return;

                  if (selectedRegistrationIdentifier) {
                    setValue('registrationName', '');
                    setSelectedRegistrationIdentifier(null);
                  }
                  setValue('jurisdiction', value);
                  clearErrors('jurisdiction');
                }}
                value={watch('jurisdiction')}
              >
                <SelectTrigger
                  className={errors.jurisdiction ? 'border-red-500' : ''}
                  disabled={isEditMode}
                >
                  <SelectValue placeholder="Select jurisdiction" />
                </SelectTrigger>
                <SelectContent>
                  {jurisdictionOptions.map((jurisdiction) => (
                    <SelectItem
                      key={jurisdiction.value}
                      value={jurisdiction.value}
                    >
                      {jurisdiction.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.jurisdiction && (
                <InputErrorMessage message={errors.jurisdiction.message} />
              )}
            </FieldWrapper>

            <FieldWrapper>
              <Label>Registration name</Label>
              <ComboboxWithSearch
                inputValue={
                  watch('registrationName') || selectedRegistrationIdentifier
                }
                setInputValue={(val) => {
                  clearErrors('registrationName');
                  clearErrors('jurisdiction');
                  setValue('registrationName', val);
                  setSelectedRegistrationIdentifier(val);
                }}
                onChangeInputValue={(val: string) => {
                  clearErrors('registrationName');
                  clearErrors('jurisdiction');
                  setValue('registrationName', val);
                  setSelectedRegistrationIdentifier(null);
                }}
                disabled={isEditMode}
                options={registrationNameOptions}
                error={errors?.registrationName?.message}
                placeholder="Enter registration name"
                {...(isEditMode && {
                  // The field holds the content id; a reader wants the name it stands for.
                  displayValueIfDisabled:
                    registrationResponse?.registration?.displayName ??
                    watch('registrationName'),
                })}
                maxHeight={240}
              />
              <p className="text-sm text-gray-500">
                Required for businesses selling taxable goods or services.
              </p>
              {errors.jurisdiction && (
                <InputErrorMessage message="Please select a registration name" />
              )}
            </FieldWrapper>

            <FieldWrapper>
              <Label>Registration number</Label>
              <Input
                {...register('registrationNumber')}
                type="text"
                placeholder="Enter registration number"
                error={errors?.registrationNumber?.message}
              />
            </FieldWrapper>

            <div className="grid grid-cols-1 gap-2xl">
              <FieldWrapper>
                <Label>
                  Effective date
                  <Tooltip content="When this registration became active">
                    <Icons.InfoCircle className="ml-1 h-4 w-4 inline text-fg-tertiary" />{' '}
                  </Tooltip>
                </Label>
                <Input
                  {...register('effectiveDate')}
                  type="date"
                  placeholder="Select date"
                  error={errors?.effectiveDate?.message}
                />
              </FieldWrapper>
            </div>

            <div className="grid grid-cols-1 gap-2xl md:grid-cols-2">
              <FieldWrapper>
                <Label>
                  Status
                  <Tooltip
                    content="Current status of this registration"
                    side="right"
                  >
                    <Icons.InfoCircle className="ml-1 h-4 w-4 inline text-fg-tertiary" />{' '}
                  </Tooltip>
                </Label>
                <Select
                  onValueChange={(value: RegistrationValidationStatus) => {
                    clearErrors('status');
                    setValue('status', value);
                  }}
                  value={watch('status') || undefined}
                >
                  <SelectTrigger error={errors.status?.message}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {RegistrationHelper.getRegistrationValidationStatusOptions().map(
                      (option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {errors.status && (
                  <InputErrorMessage message={errors.status.message} />
                )}
              </FieldWrapper>

              <FieldWrapper>
                <Label>
                  Linked credential (optional)
                  <Tooltip content="Associate this registration with a document or credential">
                    <Icons.InfoCircle className="ml-1 h-4 w-4 inline text-fg-tertiary" />{' '}
                  </Tooltip>
                </Label>
                <Select
                  onValueChange={(value) => setValue('linkedCredential', value)}
                  defaultValue={watch('linkedCredential')}
                  value={watch('linkedCredential')}
                >
                  <SelectTrigger
                    disabled={
                      credentials.length === 0 ||
                      !!registrationResponse?.registration?.credentials?.[0]?.id
                    }
                  >
                    <SelectValue placeholder="Select credential" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[240px]">
                    {credentials.map((credential) => (
                      <SelectItem
                        key={credential.value}
                        value={credential.value}
                      >
                        {credential.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrapper>

              <FieldWrapper _className="col-span-2">
                <Label>Registration document</Label>
                <SingleFileUploader
                  companyId={companyId}
                  hideDefaultActions={isLegacyDocument ? ['delete'] : []}
                  file={
                    !isReplacingLegacyDocument &&
                    registrationDocument?.fileName &&
                    registrationDocument?.fileUrl
                      ? {
                          id:
                            registrationDocument.fileUrl === initialDocument?.fileUrl
                              ? existingDocument?.fileId ?? legacyDocument?.id
                              : undefined,
                          fileName: registrationDocument.fileName,
                          fileUrl: registrationDocument.fileUrl,
                          documentType: LegacyDocumentType.Other,
                        }
                      : null
                  }
                  setFile={(file: FileBasic) => {
                    if (file) {
                      clearErrors('registrationDocument');
                      setIsReplacingLegacyDocument(false);
                      setValue('registrationDocument', {
                        fileName: file.fileName,
                        fileUrl: file.fileUrl,
                      });
                    } else {
                      setValue('registrationDocument', {
                        fileName: '',
                        fileUrl: '',
                      });
                    }
                  }}
                  onFileChange={(file: FileBasic) => {
                    if (file) {
                      clearErrors('registrationDocument');
                      setValue('registrationDocument', {
                        fileName: file.fileName,
                        fileUrl: file.fileUrl,
                      });
                    } else {
                      setValue('registrationDocument', {
                        fileName: '',
                        fileUrl: '',
                      });
                    }
                  }}
                />
                {isLegacyDocument && !isReplacingLegacyDocument && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setIsReplacingLegacyDocument(true)}
                  >
                    Replace document
                  </Button>
                )}
                {(errors.registrationDocument?.fileName?.message ||
                  errors.registrationDocument?.fileUrl?.message ||
                  errors.registrationDocument?.message) && (
                  <InputErrorMessage message="Please add a registration document" />
                )}
              </FieldWrapper>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleClose}
              size="sm"
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isSubmitting.value}
            >
              {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
              <span>Confirm</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
